import type { Seat } from '@campfire/engine';
import { HOME, SAFE0, SIDE_LEN, SLIDES, START, type SorryAction, type SorryEvent, type SorryMove, type SorryView } from '@campfire/game-sorry';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { pawnShape, seatColor, svg } from './track-common.ts';

const U = 24;
const BOARD = 16 * U;
const NEUTRAL = '#9a9a9a';

/** Rotate a point (y, x in cell units) a quarter turn clockwise about the board centre, k times. */
function rot(k: number, y: number, x: number): [number, number] {
  let p: [number, number] = [y, x];
  for (let i = 0; i < k; i++) p = [p[1], 16 - p[0]];
  return p;
}

/** Cell-unit centre of a pawn position; `slot` spreads pawns sharing Start or Home. */
export function spot(side: number, pos: number, slot: number): [number, number] {
  const off = (n: number, a: number, b: number): [number, number] => [a + (n >> 1) * 1.2, b + (n % 2) * 1.2];
  if (pos === START) return rot(side, ...off(slot, 2.1, 5.2));
  if (pos === HOME) return rot(side, 6.5 + ((slot >> 1) - 0.5) * 0.7, 2.5 + ((slot % 2) - 0.5) * 0.7);
  if (pos >= SAFE0) return rot(side, 1.5 + (pos - SAFE0), 2.5);
  return rot(Math.floor(pos / SIDE_LEN), 0.5, (pos % SIDE_LEN) + 0.5);
}
const px = (p: [number, number]) => ({ x: p[1] * U, y: p[0] * U });

const ruleText = (c: number, again: boolean): string => (CARD_HINT[c] ?? '').replace(/^Sorry! /, '').replace(' Then draw again.', again ? ' Draw again.' : '');

export const CARD_HINT: Record<number, string> = {
  0: 'Sorry! Take a pawn from Start and bump an opponent pawn.',
  1: 'Leave Start, or move one pawn 1.',
  2: 'Leave Start, or move one pawn 2. Then draw again.',
  3: 'Move one pawn 3.',
  4: 'Move one pawn 4 backward.',
  5: 'Move one pawn 5.',
  7: 'Move one pawn 7, or split 7 between two pawns.',
  8: 'Move one pawn 8.',
  10: 'Move one pawn 10, or 1 backward.',
  11: 'Move one pawn 11, or swap with an opponent.',
  12: 'Move one pawn 12.',
};
export const cardName = (c: number): string => (c === 0 ? 'Sorry!' : String(c));

function boardStatic(v: SorryView): SVGElement[] {
  const parts: SVGElement[] = [svg('rect', { x: 0, y: 0, width: BOARD, height: BOARD, rx: 10, class: 'so-bg' })];
  for (let k = 0; k < 4; k++) {
    const seat = v.sides.indexOf(k);
    const col = seat >= 0 ? seatColor(seat) : NEUTRAL;
    const g: SVGElement[] = [];
    for (let i = 0; i < SIDE_LEN; i++) g.push(svg('rect', { x: i * U, y: 0, width: U, height: U, class: 'so-sq' }));
    for (const sl of SLIDES.filter((s) => s.side === k)) {
      const rel = sl.start - k * SIDE_LEN;
      const len = sl.end - sl.start + 1;
      g.push(
        svg('rect', { x: rel * U + 1, y: 1, width: len * U - 2, height: U - 2, rx: 6, fill: col, class: 'so-slide', 'data-slide': `${k}-${rel}` }),
        svg('polygon', { points: `${rel * U + 5},${U / 2 - 6} ${rel * U + 15},${U / 2} ${rel * U + 5},${U / 2 + 6}`, class: 'so-slide-head' }),
        svg('polygon', { points: `${(rel + len) * U - 4},${U / 2 - 5} ${(rel + len) * U - 12},${U / 2} ${(rel + len) * U - 4},${U / 2 + 5}`, class: 'so-slide-end' }),
      );
    }
    for (let i = 0; i < 5; i++) g.push(svg('rect', { x: 2 * U + 2, y: (1 + i) * U + 2, width: U - 4, height: U - 4, rx: 4, fill: col, class: 'so-safe' }));
    g.push(
      svg('circle', { cx: 2.5 * U, cy: 6.5 * U, r: 0.8 * U, fill: col, class: 'so-home' }),
      svg('circle', { cx: 5.8 * U, cy: 2.7 * U, r: 1.55 * U, fill: col, class: 'so-start' }),
    );
    parts.push(svg('g', { transform: `rotate(${90 * k} ${BOARD / 2} ${BOARD / 2})`, 'data-side': k }, ...g));
    const label = (p: [number, number], t: string) => svg('text', { x: px(p).x, y: px(p).y + 3, class: 'so-label' }, t);
    parts.push(label(rot(k, 4.7, 5.8), seat >= 0 ? 'START' : ''), label(rot(k, 7.6, 2.5), seat >= 0 ? 'HOME' : ''));
  }
  return parts;
}

function centreCard(v: SorryView): SVGElement {
  const x = BOARD / 2 - 40;
  const y = BOARD / 2 - 52;
  return svg('g', { 'data-testid': 'sorry-card', 'data-card': v.card ?? '' },
    svg('rect', { x, y, width: 80, height: 104, rx: 9, class: v.card === null ? 'so-card back' : 'so-card' }),
    svg('text', { x: BOARD / 2, y: BOARD / 2 + (v.card === 0 ? 4 : 14), class: v.card === 0 ? 'so-card-txt small' : 'so-card-txt' }, v.card === null ? '?' : cardName(v.card)),
    svg('text', { x: BOARD / 2, y: y + 96, class: 'so-deck' }, `${v.deckCount} left`));
}

const moveKey = (m: SorryMove) => JSON.stringify(m.action);
const involves = (m: SorryMove, pawn: number) => {
  const a = m.action;
  return a.type === 'split' ? a.pawn === pawn || a.pawn2 === pawn : 'pawn' in a && a.pawn === pawn;
};

export function moveText(ctx: TableCtx, m: SorryMove): string {
  const a = m.action;
  const bump = m.bumps.length ? ` · bumps ${m.bumps.map((b) => `${name(ctx, b.seat)} pawn ${b.pawn + 1}`).join(', ')}` : '';
  const slide = m.slid ? ' · slide!' : '';
  const last = m.steps[0];
  const end = last && last.to === HOME ? ' · Home' : last && last.to >= SAFE0 && last.from < SAFE0 ? ' · safety zone' : '';
  switch (a.type) {
    case 'move':
      return last && last.from === START ? `Pawn ${a.pawn + 1} out of Start${bump}` : `Pawn ${a.pawn + 1} ${a.by > 0 ? 'forward' : 'back'} ${Math.abs(a.by)}${bump}${slide}${end}`;
    case 'split':
      return `Split: pawn ${a.pawn + 1} ${a.by}, pawn ${a.pawn2 + 1} ${a.by2}${bump}${slide}${m.steps.some((s) => s.to === HOME) ? ' · Home' : ''}`;
    case 'swap':
      return `Swap pawn ${a.pawn + 1} with ${name(ctx, a.seat)} pawn ${a.opp + 1}${slide}${bump}`;
    case 'sorry':
      return `Sorry! Pawn ${a.pawn + 1} takes ${name(ctx, a.seat)} pawn ${a.opp + 1}${slide}${bump}`;
    default:
      return 'Skip';
  }
}

export function sorryEvent(ctx: TableCtx, e: SorryEvent | null, turn: Seat): string {
  if (!e) return `${name(ctx, turn)} ${turn === ctx.you ? 'go' : 'goes'} first. Draw a card!`;
  const who = name(ctx, e.seat);
  const card = cardName(e.card);
  if (e.kind === 'drew') return `${who} drew ${card}. ${CARD_HINT[e.card] ?? ''}`;
  if (e.kind === 'forfeit') return `${who} drew ${card} but cannot move. Turn forfeited.`;
  if (e.kind === 'passed') return `${who} drew ${card} and chose not to swap.`;
  const bumps = e.bumps.map((b) => `${name(ctx, b.seat)} pawn ${b.pawn + 1} is sent back to Start`).join('; ');
  return `${who} played ${card}${e.slid ? ' and slid' : ''}.${bumps ? ` ${bumps}.` : ''}${e.again ? ' Draws again!' : ''}`;
}

export const sorryUi: GameUi = {
  id: 'sorry',
  privateHands: false,
  seatInfo: (ctx, s) => {
    const row = (ctx.msg.view as SorryView).pawns[s] ?? [];
    return `${row.filter((p) => p === HOME).length}/${row.length} home`;
  },
  render(ctx) {
    const v = ctx.msg.view as SorryView;
    const over = v.result !== null;
    const mine = ctx.myTurn && !over;
    const key = JSON.stringify([v.pawns, v.card, v.turn]);
    const movable = [...new Set(v.legal.flatMap((m) => (m.action.type === 'split' ? [m.action.pawn, m.action.pawn2] : 'pawn' in m.action ? [m.action.pawn] : [])))].sort();
    const stored = ctx.local.get<{ key: string; pawn: number }>('sel');
    const sel = stored && stored.key === key && movable.includes(stored.pawn) ? stored.pawn : movable.length === 1 ? (movable[0] as number) : null;
    const select = (pawn: number) => ctx.local.set('sel', { key, pawn });
    const shown = sel === null ? [] : v.legal.filter((m) => involves(m, sel));
    const act = (a: SorryAction) => ctx.act(a);

    const pawns: SVGElement[] = [];
    v.pawns.forEach((row, s) => {
      const side = v.sides[s] as number;
      const slots = new Map<number, number>();
      row.forEach((pos, i) => {
        const slot = slots.get(pos) ?? 0;
        slots.set(pos, slot + 1);
        const slotIdx = pos === START || pos === HOME ? i : slot;
        const { x, y } = px(spot(side, pos, slotIdx));
        // Pawns in Start are interchangeable: tapping any of them picks the one the move uses.
        const pick = pos === START ? row.findIndex((q, j) => q === START && movable.includes(j)) : i;
        const can = mine && s === v.turn && pick >= 0 && movable.includes(pick);
        const g = svg('g', {
          class: `so-pawn${can ? ' can' : ''}${can && sel === pick ? ' sel' : ''}`,
          'data-testid': `sorry-pawn-${s}-${i}`, 'data-pos': pos,
          role: can ? 'button' : undefined, 'aria-label': `${name(ctx, s)} pawn ${i + 1}`,
        }, pawnShape(s, x, y, pos === HOME ? 7 : 9.5, { class: 'so-body' }), svg('text', { x, y: y + 4, class: 'so-num' }, String(i + 1)));
        if (can) g.addEventListener('click', () => select(pick));
        pawns.push(g);
      });
    });
    const dests: SVGElement[] = [];
    if (mine && sel !== null) {
      shown.forEach((m, k) => {
        if (m.action.type === 'split') return;
        const step = m.steps[0];
        if (!step) return;
        const side = v.sides[v.turn] as number;
        const { x, y } = px(spot(side, step.to, 0));
        // Big labelled target on the square the pawn ends on; a slide also shows its path (FR-SORRY-17).
        const a = m.action;
        const label = a.type === 'move' ? (step.from === START ? 'Out' : a.by > 0 ? `+${a.by}` : `−${-a.by}`) : a.type === 'swap' ? '⇄' : '!';
        const slide = m.slid ? SLIDES.find((sl) => sl.end === step.to && sl.side !== side) : undefined;
        const path = slide ? (() => { const f = px(spot(side, slide.start, 0)); return svg('line', { x1: f.x, y1: f.y, x2: x, y2: y, class: 'so-dest-slide' }); })() : null;
        const t = svg('g', { class: 'so-dest', 'data-testid': `sorry-dest-${k}`, role: 'button', 'aria-label': moveText(ctx, m) },
          ...(path ? [path] : []), svg('circle', { cx: x, cy: y, r: 16 }), svg('text', { x, y: y + 4.5, class: 'so-dest-txt' }, label));
        t.addEventListener('click', () => act(m.action));
        dests.push(t);
      });
    }

    return h(
      'div.sorry-table',
      {},
      h('div.so-panel', {},
        h('div.event', { 'data-testid': 'event' }, sorryEvent(ctx, v.last, v.turn)),
        h('button.primary.so-draw', { 'data-testid': 'sorry-draw', disabled: !(mine && v.phase === 'draw'), onclick: () => act({ type: 'draw' }) },
          over ? 'Game over' : mine ? (v.phase === 'draw' ? 'Draw a card' : 'Choose a move') : `${name(ctx, v.turn)} plays…`),
        // The card just drawn, in one line; the full card list sits under the board (FR-SORRY-18/19).
        v.card !== null && v.card !== undefined && !over ? h('div.so-now', { 'data-testid': 'sorry-now' }, h('b', {}, cardName(v.card)), ' ', ruleText(v.card, v.drawAgain)) : null,
        mine && v.phase === 'move' && movable.length > 1
          ? h('div.so-chips', { 'data-testid': 'sorry-pawns' }, movable.map((i) => h(`button.so-chip${sel === i ? '.on' : ''}`, { 'data-testid': `sorry-chip-${i}`, onclick: () => select(i) }, `Pawn ${i + 1}`)))
          : null,
        mine && v.phase === 'move'
          ? h('div.so-moves', { 'data-testid': 'sorry-moves' }, [
              ...shown.map((m, k) => h('button.so-move', { 'data-testid': `sorry-move-${k}`, 'data-action': moveKey(m), onclick: () => act(m.action) }, moveText(ctx, m))),
              sel === null && movable.length > 1 ? h('div.muted', {}, 'Pick a pawn (tap it or a button) to see its moves.') : null,
              v.canPass ? h('button.so-move', { 'data-testid': 'sorry-pass', onclick: () => act({ type: 'pass' }) }, 'Skip (no swap)') : null,
            ])
          : null,
      ),
      h('div.so-board-wrap', {}, svg('svg', { viewBox: `0 0 ${BOARD} ${BOARD}`, class: 'so-board', 'data-testid': 'sorry-board', role: 'img', 'aria-label': 'Sorry! board' },
        ...boardStatic(v), centreCard(v), ...pawns, ...dests)),
      h('details.so-rules', { 'data-testid': 'sorry-legend' }, h('summary', {}, 'What each card does'),
        h('div.so-cards', { 'data-testid': 'sorry-cards' }, [1, 2, 3, 4, 5, 7, 8, 10, 11, 12, 0].map((c) =>
          h(`div.so-cardrule${c === v.card ? '.on' : ''}`, { 'data-card-rule': c }, h('b', {}, cardName(c)), ' ', ruleText(c, v.drawAgain))))),
      h('div.so-legend', {}, v.pawns.map((row, s) =>
        h(`div.so-key${s === v.turn && !over ? '.active' : ''}`, { 'data-testid': `key-${s}` }, h('svg.so-swatch', { viewBox: '-12 -12 24 24' }, pawnShape(s, 0, 0, 8)), `${name(ctx, s)} · ${row.filter((p) => p === HOME).length}/${row.length} home`))),
      h('div.muted', {}, v.drawAgain ? 'A 2 draws again.' : ''),
    );
  },
};
